import React, { useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { getModuleDisplayName } from "src/adminPanel/modules/ModulesSettings";
import { useCreateModule } from "src/api/workspacesV2Api";
import { Button } from "src/base-components/Button";
import { Combobox } from "src/base-components/Combobox";
import { WorkspaceDb } from "src/clients/flow-api";
import { OrganizationInDB } from "src/clients/taktile-api";
import { Callout } from "src/design-system/Callout";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { logger } from "src/utils/logger";
import { useFuseSearch } from "src/utils/useFuseSearch";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  moduleType: string;
  workspaces: WorkspaceDb[];
  orgMap: Map<string, OrganizationInDB>;
  afterLeave?: () => void;
};

type WorkspaceOption = {
  key: string;
  value: React.ReactNode;
  searchText: string;
  workspace: WorkspaceDb;
};

export const ModuleCreateModal: React.FC<Props> = ({
  isOpen,
  onClose,
  moduleType,
  workspaces,
  orgMap,
  afterLeave,
}) => {
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>("");
  const [query, setQuery] = useState("");
  const createModule = useCreateModule();

  // Filter out workspaces that already have this module
  const availableWorkspaces = workspaces.filter((workspace) => {
    const modules = workspace.modules as Record<string, unknown>;
    return !modules?.[moduleType];
  });

  // Create searchable options for the combobox
  const workspaceOptions: WorkspaceOption[] = availableWorkspaces.map(
    (workspace) => {
      const org = orgMap.get(workspace.organization_id);
      const orgName = org?.name || workspace.organization_id;

      return {
        key: workspace.id,
        value: (
          <div>
            <div className="font-inter-semibold-12px">{workspace.name}</div>
            <div className="font-inter-normal-12px text-gray-500">
              {workspace.slug}
            </div>
            <div className="font-inter-normal-12px text-gray-400">
              {orgName}
            </div>
          </div>
        ),
        searchText: `${workspace.name} ${workspace.slug} ${workspace.id} ${workspace.organization_id} ${orgName}`,
        workspace,
      };
    },
  );

  // Use fuse search for filtering options
  const search = useFuseSearch(workspaceOptions, {
    keys: ["searchText"],
    threshold: 0.3,
  });

  const filteredOptions = query.length >= 2 ? search(query) : workspaceOptions;

  const handleCreate = async () => {
    if (!selectedWorkspaceId) return;

    const selectedWorkspace = availableWorkspaces.find(
      (ws) => ws.id === selectedWorkspaceId,
    );

    if (!selectedWorkspace) return;

    try {
      await createModule.mutateAsync({
        workspaceId: selectedWorkspaceId,
        moduleType,
      });

      toastActions.success({
        title: "Module created successfully",
        description: `${getModuleDisplayName(moduleType)} module has been created for ${selectedWorkspace.name}`,
      });

      handleClose();
    } catch (error) {
      logger.error("Failed to create module", error);
      toastActions.failure({
        title: "Failed to create module",
        description: "Please try again or start a thread in #team-platform",
      });
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedWorkspaceId("");
    setQuery("");
  };

  const selectedWorkspace = availableWorkspaces.find(
    (ws) => ws.id === selectedWorkspaceId,
  );

  const hasAgentModule = (workspace: WorkspaceDb) => {
    const modules = workspace.modules as Record<string, unknown>;
    return !!modules?.["agents"];
  };

  const requiresAgentModule =
    moduleType === "case_management" &&
    selectedWorkspace &&
    !hasAgentModule(selectedWorkspace);

  return (
    <Modal
      afterLeave={afterLeave}
      open={isOpen}
      size="md"
      onClose={handleClose}
    >
      <Modal.Header>
        Create {getModuleDisplayName(moduleType)} Module
      </Modal.Header>
      <Modal.Content>
        <div className="mb-20 space-y-4">
          <p className="font-inter-normal-13px text-gray-600">
            Select a workspace to create the{" "}
            <span className="font-inter-semibold-13px">
              {getModuleDisplayName(moduleType).toLowerCase()}
            </span>{" "}
            module for. Only workspaces that don't already have this module
            created are shown.
          </p>

          <div className="space-y-2">
            <label className="block font-inter-semibold-13px text-gray-700">
              Select Workspace
            </label>
            <Combobox
              dropdownPlaceholder={
                <div className="px-4 py-8 text-center font-inter-normal-13px text-gray-500">
                  {(() => {
                    if (availableWorkspaces.length === 0) {
                      return "All workspaces already have this module created";
                    }
                    return "No workspaces match your search";
                  })()}
                </div>
              }
              options={filteredOptions}
              placeholder="Search by workspace name, slug, ID, or organization..."
              value={selectedWorkspaceId}
              onChange={(value) => setSelectedWorkspaceId(value || "")}
            />
          </div>

          {selectedWorkspace && (
            <div className="rounded-lg border bg-gray-50 p-4">
              <h3 className="mb-4 font-inter-semibold-13px text-gray-900">
                Selected Workspace Details
              </h3>

              <div className="grid grid-cols-1 gap-4 font-inter-normal-13px">
                <div className="space-y-3">
                  <div>
                    <div className="font-inter-medium-12px text-gray-500 uppercase tracking-wide">
                      Organization
                    </div>
                    <a
                      className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      href={`/admin-panel/organizations?search=${selectedWorkspace.organization_id}`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {orgMap.get(selectedWorkspace.organization_id)?.name ||
                        selectedWorkspace.organization_id}
                    </a>
                    <div className="font-code-12 text-gray-500">
                      ID: {selectedWorkspace.organization_id}
                    </div>
                  </div>

                  <div>
                    <div className="font-inter-medium-12px text-gray-500 uppercase tracking-wide">
                      Workspace
                    </div>
                    <a
                      className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      href={`/admin-panel/organizations?search=${selectedWorkspace.id}`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {selectedWorkspace.name}
                    </a>
                    <div className="font-code-12 text-gray-500">
                      Slug: {selectedWorkspace.slug}
                    </div>
                    <div className="font-code-12 text-gray-500">
                      ID: {selectedWorkspace.id}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {requiresAgentModule && (
            <Callout type="warning">
              Agent module has to be enabled in order to create case management
              module for this workspace.
            </Callout>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer
        primaryButton={
          <Button
            disabled={
              !selectedWorkspaceId ||
              createModule.isPending ||
              requiresAgentModule
            }
            iconLeft={faPlus}
            loading={createModule.isPending}
            variant="primary"
            onClick={handleCreate}
          >
            {createModule.isPending ? "Creating..." : "Create Module"}
          </Button>
        }
        secondaryButton="Cancel"
      />
    </Modal>
  );
};
